import { Button } from 'components/ui/button';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { t } from 'utils/getTranslations';
import { isPersonalCodeMask } from 'utils/personalCodeMask';

/** The request for a personal ID code in clear: a lazy query's trigger, which resolves with its data or its error. */
export type PersonalCodeRequest = () => PromiseLike<{ data?: { value: string | null } }>;

/**
 * The "Show" link after a personal ID code read as asterisks. It hands the code in clear to `onShow`, which keeps it
 * in the view or form that asked: it never reaches the record's cached detail.
 */
export function PersonalCodeShowLink({
    request,
    onShow,
}: {
    request: PersonalCodeRequest;
    onShow: (code: string) => void;
}) {
    const [busy, setBusy] = useState(false);
    return (
        <Button
            variant="link"
            className="text-link h-auto p-0 text-xs font-normal"
            aria-label={t('personal-code.show-label')}
            disabled={busy}
            focusableWhenDisabled
            onClick={() => {
                setBusy(true);
                void request().then(({ data }) => {
                    setBusy(false);
                    if (data) onShow(data.value ?? '');
                });
            }}
        >
            <FormattedMessage id="personal-code.show" />
        </Button>
    );
}

/** A read view's personal ID code: the asterisks with a "Show" link for a viewer who may show it, then the code. */
export function PersonalCodeValue({
    code,
    canShow,
    request,
}: {
    code: string | null | undefined;
    canShow: boolean;
    request: PersonalCodeRequest;
}) {
    const [shown, setShown] = useState<string>();
    const shownRef = useRef<HTMLSpanElement>(null);
    // The link is gone once the code shows: focus moves to the code, so it does not fall to the page and is read out.
    useEffect(() => {
        if (shown !== undefined) shownRef.current?.focus();
    }, [shown]);
    if (shown !== undefined) {
        return (
            <span ref={shownRef} tabIndex={-1} className="outline-none">
                {shown || '-'}
            </span>
        );
    }
    if (canShow && isPersonalCodeMask(code)) {
        return (
            <span className="inline-flex items-baseline gap-2">
                {code}
                <PersonalCodeShowLink request={request} onShow={setShown} />
            </span>
        );
    }
    return <>{code || '-'}</>;
}
